import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { swipeTab } from '@/lib/modern'
import { reducedMotion, rubber, velocity } from '@/lib/motion'

// Swiping between the tabs. The page follows the finger; let go far enough (or with a flick) and it slides off that
// way while the next tab's page slides in behind it, otherwise it springs back. Only a swipe over the tabs or the page
// under them counts, and not one that starts at the screen's edge (the browser's own back) or in something that
// scrolls sideways itself (a shelf of albums). Once a touch is plainly up-down, it's a scroll and left alone.
// `root` goes on what holds both, `tabRow` on the tabs' row and `pager` on the page under them. `onTurn` changes the
// tab while the page is off screen; `onLanded` follows once the new page has slid in.
export function useTabSwipe<K extends string>({
  keys,
  active,
  still,
  onTurn,
  onLanded,
}: {
  keys: K[]
  active: K
  // Nothing open over the page and nothing being picked, so a swipe is for the tabs.
  still: boolean
  onTurn: (key: K) => void
  onLanded?: () => void
}) {
  // A callback ref, so the touches are listened for on whatever element holds the tabs, even one that comes later.
  const [el, root] = useState<HTMLDivElement | null>(null)
  const tabRow = useRef<HTMLDivElement>(null)
  const pager = useRef<HTMLDivElement>(null)
  const now = useRef({ keys, active, still, onTurn, onLanded })
  // What the touch handlers below need from this render, kept up to date after each one.
  useLayoutEffect(() => {
    now.current = { keys, active, still, onTurn, onLanded }
  })
  useEffect(() => {
    if (!el) return
    let start: { x: number; y: number } | null = null
    let lock: 'x' | 'y' | null = null
    let points: { t: number; x: number; y: number }[] = []
    let timer = 0
    const place = (x: number, transition = 'none') => {
      const p = pager.current
      if (!p) return
      p.style.transition = transition
      p.style.transform = x ? `translate3d(${x}px, 0, 0)` : ''
      p.style.opacity = x ? String(1 - Math.min(Math.abs(x) / el.clientWidth, 1) * 0.45) : ''
    }
    // The line under the tabs, part of the way from this tab to the next one the finger is pulling towards.
    const line = (x: number, transition = 'none') => {
      const row = tabRow.current
      const ind = row?.querySelector<HTMLElement>('.mp-chip-ind')
      const { keys, active } = now.current
      const at = keys.indexOf(active)
      const here = row?.querySelector<HTMLElement>(`[data-tab="${keys[at]}"]`)
      if (!ind || !here) return
      const k = Math.max(-1, Math.min(1, -x / el.clientWidth))
      const there = row?.querySelector<HTMLElement>(`[data-tab="${keys[at + Math.sign(k)]}"]`) ?? here
      const f = Math.abs(k)
      ind.style.transition = transition
      ind.style.width = `${here.offsetWidth + (there.offsetWidth - here.offsetWidth) * f}px`
      ind.style.transform = `translateX(${here.offsetLeft + (there.offsetLeft - here.offsetLeft) * f}px)`
    }
    const settled = () => {
      const ind = tabRow.current?.querySelector<HTMLElement>('.mp-chip-ind')
      if (ind) ind.style.transition = ''
    }
    const back = () => {
      place(0, 'transform 0.32s var(--mp-spring), opacity 0.32s ease')
      line(0, 'transform 0.32s var(--mp-spring), width 0.32s var(--mp-spring)')
      window.clearTimeout(timer)
      timer = window.setTimeout(settled, 350)
    }
    const onStart = (e: globalThis.TouchEvent) => {
      const t = e.touches[0]
      const target = e.target as Element
      const edge = 24
      start =
        t &&
        e.touches.length === 1 &&
        now.current.still &&
        t.clientX > edge &&
        t.clientX < window.innerWidth - edge &&
        (pager.current?.contains(target) || tabRow.current?.contains(target)) &&
        !scrollsSideways(target, el)
          ? { x: t.clientX, y: t.clientY }
          : null
      lock = null
      points = t ? [{ t: e.timeStamp, x: t.clientX, y: t.clientY }] : []
    }
    const onMove = (e: globalThis.TouchEvent) => {
      const t = e.touches[0]
      if (!start || !t) return
      const dx = t.clientX - start.x
      const dy = t.clientY - start.y
      if (!lock) {
        if (Math.hypot(dx, dy) < 10) return
        lock = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y'
        if (lock === 'y') {
          start = null
          return
        }
      }
      // Sideways now: the page doesn't scroll up or down under the finger as well.
      if (e.cancelable) e.preventDefault()
      points = [...points.slice(-5), { t: e.timeStamp, x: t.clientX, y: t.clientY }]
      const { keys, active } = now.current
      const at = keys.indexOf(active)
      const beyond = dx < 0 ? at === keys.length - 1 : at === 0
      place(beyond ? rubber(dx, 60) : dx)
      line(beyond ? 0 : dx)
    }
    const onEnd = (e: globalThis.TouchEvent) => {
      const t = e.changedTouches[0]
      const from = start
      start = null
      if (!from || lock !== 'x' || !t) return
      const dx = t.clientX - from.x
      const next = swipeTab(now.current.keys, now.current.active, dx, 0, velocity(points).x)
      if (!next) return back()
      if (reducedMotion()) {
        place(0)
        now.current.onTurn(next)
        now.current.onLanded?.()
        settled()
        return
      }
      const way = dx < 0 ? -1 : 1
      const width = el.clientWidth
      place(way * width, 'transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s ease')
      line(way * width, 'transform 0.2s ease-out, width 0.2s ease-out')
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        flushSync(() => now.current.onTurn(next))
        place(-way * width * 0.3)
        requestAnimationFrame(() =>
          requestAnimationFrame(() => place(0, 'transform 0.36s var(--ease-page), opacity 0.36s ease')),
        )
        timer = window.setTimeout(() => {
          now.current.onLanded?.()
          settled()
        }, 400)
      }, 200)
    }
    const onCancel = () => {
      if (start && lock === 'x') back()
      start = null
    }
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onCancel)
    return () => {
      window.clearTimeout(timer)
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onCancel)
    }
  }, [el])
  return { root, tabRow, pager }
}

// Whether a touch began in something that scrolls sideways on its own, between where it began and the page.
function scrollsSideways(from: Element, page: Element) {
  for (let el: Element | null = from; el && el !== page; el = el.parentElement)
    if (el.scrollWidth > el.clientWidth + 1 && /auto|scroll/.test(getComputedStyle(el).overflowX)) return true
  return false
}
